import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import { Link } from 'react-router-dom';
import FormRadioButton from '../../base/form/FormRadioButton';
import CreditCardInput from 'react-credit-card-input';
import { autorun } from 'mobx';
import Localized from '../../base/i18n/Localized';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import FormError from '../../base/form/FormError';
import ReactLoading from 'react-loading';
import FormCheckboxButton from '../../base/form/FormChecboxButton';
import Modal from 'react-modal';
import { initiate3DSDataColletion } from '../../../utils/Utils';

import imgCardPreview from '../../../assets/img/card-preview.jpg';

const Upgrade = inject('rootStore')(observer(
	class Upgrade extends Component {

		constructor(props) {
			super(props);

			this.upgradeRef = React.createRef();
			this.cardPreviewRef = React.createRef();

			this.handleUpgradeClick = this.handleUpgradeClick.bind(this);

			this.levelClasses = this.levelClasses.bind(this);
			this.scrollToTop = this.scrollToTop.bind(this);

			this.mobxAutorunDisposer = autorun(() => {
				const { pageTitlePrefix, translateMessage } = props.rootStore.commonStore;

				document.title = (pageTitlePrefix ? pageTitlePrefix + ' - ' : '') + translateMessage('page.upgrade.title');
			});

			this.state = {
				cpFontSize: 22,
				cpTop: 120,
				cpLeft: 45,
				
				// 3DS
				challengeOpen: false,
				stepUpUrl: null,
				accessToken: null
			};

			// Used as device fingerprinting reference
			this.referenceId = null;
			this.authenticationTransactionId = null;

			// Data collection (these need to be reset after each attempt!)
			this.dataCollectionTimeout = null;
			this.dataCollectionCompleted = false;
			this.enrollmentCheckInitiated = false;
		}

		componentDidMount() {
			const { userStore, contentStore } = this.props.rootStore;

			// Redirect /account/upgrade to the external membership management portal,
			// mirroring the "Manage Membership" SSO link (same tab, with SSO token).
			if (userStore.me) {
				window.location.replace(`${userStore.manageMemberShipURL}sign-in?token=${userStore.me.ssoToken}`);
				return;
			}

			const { me: { membershipLevel, companyCode } } = userStore;

			userStore.resetRegistrationFields();
			userStore.setLevel('');

			// Set default selected level to be one higher than current level if possible
			if (membershipLevel.id > 1 && membershipLevel.id > companyCode.companyLevelId) {
				userStore.setLevel(membershipLevel.id - 1);
			}

			userStore.setCountry('AE');

			userStore.loadCountries().then(result => {
				if (!result) {
					userStore.setError('Unable to get list of countries/nationalities, please reload the page and try again.');
				}
			});

			userStore.loadSubscriptions().then(result => {
				if (!result) {
					userStore.setError('Unable to get list of subscriptions, please reload the page and try again.');
				} else {
					// Set default subscription
					userStore.setSubscription(String(userStore.subscriptions.find(s => s.subscriptionMonths === 12).id));
				}
			});

			userStore.loadLevels().then(result => {
				if (!result) {
					userStore.setError('Unable to get list of levels, please reload the page and try again.');
				}
			});

			contentStore.loadContent('levelFeaturesDiscount', 'levelFeaturesSilver', 'levelFeaturesGold', 'levelFeaturesPlatinum');

			userStore.getLastTransactionDetails().then(details => {
				if (details) {
					userStore.setFirstName(details.billingFirstName);
					userStore.setLastName(details.billingLastName);
					userStore.setAddress(details.billingAddress);
					userStore.setCity(details.billingCity);
					userStore.setCountry(details.billingCountry);
					userStore.setPostalCode(details.billingPostalCode);
				}
			});

			window.addEventListener('resize', this.handleResize);

			this.handleResize();

			// 3DS stuff
			// Setup listener for when data collection is complete
			window.addEventListener('message', this.handleWindowMessage, false);
		}

		componentWillUnmount() {
			this.mobxAutorunDisposer();

			window.removeEventListener('resize', this.handleResize);
			window.removeEventListener('message', this.handleWindowMessage);
		}

		handleWindowMessage = event => {
			const { commonStore, userStore } = this.props.rootStore;
			const { translateMessage } = commonStore;

			if (event.origin === 'https://centinelapistag.cardinalcommerce.com' ||
					event.origin === 'https://centinelapi.cardinalcommerce.com')
			{
				console.debug('Data collection event message: ', event.data);

				const messageData = JSON.parse(event.data);
				
				if (messageData.MessageType === 'profile.completed' &&
						messageData.Status === true)
				{
					console.debug('Attempting to call enrollment check due to data collection message being received.')

					this.dataCollectionCompleted = true;

					clearTimeout(this.dataCollectionTimeout);

					this.doEnrollmentCheck(messageData);
				} else {
					userStore.setError(translateMessage('generic.error.threeDSDataCollectionError'));
				}
			}
		};

		doEnrollmentCheck = data => {	
			if (this.enrollmentCheckInitiated) {
				return;
			}

			this.enrollmentCheckInitiated = true;

			// Process response and initiate payment response via new V2 payment API
			// Based on response the payment will either go thru as before or there will be a need
			// to challenge the user by showing a popup with an iframe we post data too
			// Iframe load event will need to be monitored and intercepted for the challenge result,
			// after which we hide the iframe and finish the payment with the auth transaction ID we receive

			const { userStore } = this.props.rootStore;

			userStore.setThreeDSSetupInProgress(false);

			userStore.upgrade(this.referenceId).then(response => {
				if (response.status === "CHALLENGE_REQUIRED") {
					this.authenticationTransactionId = response.data.authenticationTransactionId;

					this.setState({
						challengeOpen: true,
						stepUpUrl: response.data.stepUpUrl,
						accessToken: response.data.accessToken
					});
				}
			});
		};

		handleResize = () => {
			const baseWidth = 485;

			if (this.cardPreviewRef.current) {
				const cr = this.cardPreviewRef.current.getBoundingClientRect();

				const wChangePercent = cr.width * 100 / baseWidth;

				this.setState({
					cpFontSize: 22 * (wChangePercent / 100),
					cpTop: 120 * (wChangePercent / 100),
					cpLeft: 45 * (wChangePercent / 100)
				});
			}
		}

		handleNameFazaaChange = e => {
			if (e.target.validity.valid) {
				this.props.rootStore.userStore.setNameFazaa(e.target.value);
			}
		}
		handleLevelChange = id => {
			this.props.rootStore.userStore.setLevel(id);
			this.props.rootStore.userStore.setSubscription(String(this.props.rootStore.userStore.subscriptions.find(s => s.subscriptionMonths === 12).id));
		}
		handleSubscriptionChange = e => this.props.rootStore.userStore.setSubscription(e.target.value);
		handleFirstNameChange = e => {
			if (new RegExp('^[a-zA-Z\\-\\s]*$').test(e.target.value)) {
				this.props.rootStore.userStore.setFirstName(e.target.value);
			}
		}
		handleLastNameChange = e => {
			if (new RegExp('^[a-zA-Z\\-\\s]*$').test(e.target.value)) {
				this.props.rootStore.userStore.setLastName(e.target.value);
			}
		}
		handleAddressChange = e => {
			if (new RegExp('^[a-zA-Z\\d\\-\\s/\\.]*$').test(e.target.value)) {
				this.props.rootStore.userStore.setAddress(e.target.value);
			}
		}
		handlePostalCodeChange = e => {
			if (new RegExp('^[a-zA-Z\\d\\-\\s]*$').test(e.target.value)) {
				this.props.rootStore.userStore.setPostalCode(e.target.value);
			}
		}
		handleCityChange = e => {
			if (new RegExp('^[a-zA-Z\\-\\s]*$').test(e.target.value)) {
				this.props.rootStore.userStore.setCity(e.target.value);
			}
		}
		handleCountryChange = e => this.props.rootStore.userStore.setCountry(e.target.value);
		handleCcNumberChange = e => this.props.rootStore.userStore.setCcNumber(e.target.value);
		handleCcExpiryChange = e => this.props.rootStore.userStore.setCcExpiry(e.target.value);
		handleCcCvvChange = e => this.props.rootStore.userStore.setCcCvv(e.target.value);
		handleTermsRefundsChange = e => this.props.rootStore.userStore.setTermsRefunds(e.target.checked);

		handleUpgradeClick(e) {
			e.preventDefault();

			const { commonStore, userStore } = this.props.rootStore;
			const { translateMessage } = commonStore;
			const { nameFazaa } = userStore;

			userStore.clearErrors();

			this.dataCollectionCompleted = false;
			this.enrollmentCheckInitiated = false;

			// Name on card has to contain at least one space character
			//if (!nameFazaa.trim().includes(' ')) {
			//	userStore.addError('nameFazaa', translateMessage('generic.error.nameMustContainSpace'));
			//}

			if (userStore.errors.length === 0) {
				userStore.upgradeSetup().then(response => {
					if (response && response.deviceDataCollectionUrl && response.accessToken) {
						// Create iframe, post to iframe and wait for event
						this.referenceId = response.referenceId;

						initiate3DSDataColletion(response.deviceDataCollectionUrl, response.accessToken);

						// Call after 10 seconds
						this.dataCollectionTimeout = setTimeout(() => {
							console.debug('Calling enrollment check due to timeout on data collection.')

							this.doEnrollmentCheck();
						}, 10000);
					} else {
						userStore.setThreeDSSetupInProgress(false);
					}
				});
			} else {
				userStore.setError(translateMessage('generic.error.checkFields'));
			}

			this.scrollToTop();
		}

		levelClasses(id) {
			const { level } = this.props.rootStore.userStore;

			if (!level) {
				return '';
			} else {
				if (level === id) {
					return 'signup__level--selected';
				} else {
					return 'signup__level--not-selected';
				}
			}
		}

		scrollToTop() {
			if (this.upgradeRef.current) {
				requestAnimationFrame(() => {
					this.upgradeRef.current.scrollIntoView({
						behavior: 'smooth',
						block: 'start'
					});
				});
			}
		}

		redirectHandler = event => {
			try {
				if (event.target.contentWindow.location.href.includes('account/upgrade')) {
					this.setState({
						challengeOpen: false
					});

					// Try to complete payment
					const { userStore } = this.props.rootStore;
					
					userStore.upgradeComplete(this.authenticationTransactionId);
				}
			} catch (e) {

			}
		}

		render() {
			const { locale, translateMessage } = this.props.rootStore.commonStore;
			const { me, countries, nameFazaa, levels, subscriptions, subscription, level, firstName, lastName, address, postalCode, city, country, ccNumber, ccExpiry, ccCvv, termsRefunds, error, success, loading, errors, threeDSSetupInProgress } = this.props.rootStore.userStore;
			//const freeUpgrade = me.companyCode ? !!me.companyCode.companyMembershipFree : false;
			const freeUpgrade = false;
			//const upgradeDisabled = !nameFazaa || !level || (!freeUpgrade && !(firstName && lastName && address && postalCode && city && country && ccNumber && ccExpiry && ccCvv));
			const upgradeDisabled = /*!nameFazaa ||*/ !level || !subscription || !(firstName && lastName && address && postalCode && city && country && ccNumber && ccExpiry && ccCvv && termsRefunds);
			const subscriptionInfo = subscriptions.find(s => String(s.id) === subscription);
			const currentLevel = levels.find(l => l.id === me.membershipLevel.id);
			const selectedLevel = levels.find(l => l.id === level);
			const { contentStore } = this.props.rootStore;
			const content = {
				levelFeaturesDiscount: contentStore.getContent('levelFeaturesDiscount'),
				levelFeaturesSilver: contentStore.getContent('levelFeaturesSilver'),
				levelFeaturesGold: contentStore.getContent('levelFeaturesGold'),
				levelFeaturesPlatinum: contentStore.getContent('levelFeaturesPlatinum')
			};
			// companyAllowSignup ignored by request via #FW-967
			let noUpgrade = me.membershipLevel.levelName === 'Platinum' /*|| !!!me.companyCode.companyAllowSignup*/ || me.companyCode.companyLevelId >= me.membershipLevel.id || me.companyCode.companyInitiative;
			noUpgrade = false;
			const { cpFontSize, cpTop, cpLeft } = this.state;

			return (
				<section ref={this.upgradeRef} className="section section--gray">
					<h2 className="section__title section__title--center">
						<Localized code="page.upgrade.title" />
					</h2>

					<div className="section__wrapper">
						<div className="upgrade">
							<div className="upgrade__form">
								<form className="form">
									{threeDSSetupInProgress && (
										<div className="form__message form__message--progress">
											<ReactLoading className="form__message-loading" type="spokes" color="#002652" height="36px" width="36px" />
											<Localized code="generic.text.threeDSInProgress" />
										</div>
									)}

									{!!error ? (
									<div className="form__message form__message--error">{error}</div>
									) : null}

									{!!success ? (
									<div className="form__section">
										<h4 className="form__title">
											<Localized code="page.upgrade.subtitle.success" />
										</h4>

										<p className="form__text">
											<Localized code="page.upgrade.text.success" />
										</p>

										<p className="form__text">
											<Link to="/account" className="button button--gold">
												<Localized code="page.upgrade.link.account" />
											</Link>
										</p>
									</div>
									) : null}

									{!!noUpgrade ? (
									<div className="form__section">
										<h4 className="form__title">
											<Localized code="page.upgrade.subtitle.notPossible" />
										</h4>

										<p className="form__text">
											<Localized code="page.upgrade.text.notPossible" />
										</p>

										<p className="form__text">
											<Link to="/account" className="button button--gold">
												<Localized code="page.upgrade.link.account" />
											</Link>
										</p>
									</div>
									) : null}

									{!success && !noUpgrade ? (
									<div className="form__section">
										<p className="form__text form__text--center">
											<Localized code="page.upgrade.text.begin" />
										</p>

										{false && (
											<div className="form__row">
												<div className="form__column form__column--half">
													<div className="form__group">
														<label className="form__label" htmlFor="nameFazaa">
															<Localized code="page.signup.label.nameOnFazaaCard" />
														</label>
														<input pattern="[a-zA-Z ]+" maxLength="26" className="form__input" type="text" id="nameFazaa" value={nameFazaa} onChange={this.handleNameFazaaChange} />
														<FormError errors={errors} field="nameFazaa" />
													</div>

													{nameFazaa ? (
														<div ref={this.cardPreviewRef} className="card-preview">
															<img className="card-preview__image" src={imgCardPreview} />
															<span style={{ fontSize: cpFontSize + 'px', top: cpTop + 'px', left: cpLeft + 'px' }} className="card-preview__text">{nameFazaa}</span>
														</div>
													) : null}
												</div>
											</div>
										)}

										<div className="form__group">
											<label className="form__label">
												<Localized code="page.signup.label.cardLevel" />
											</label>
											<div className="signup__levels">
												{levels.slice().sort((a, b) => a.levelAmount < b.levelAmount ? -1 : 1).filter(l => l.levelAmount > currentLevel.levelAmount && (l.id >= me.companyCode.companyLevelId)).map(l => (
													<div key={l.id} className={`signup__level signup__level--${l.levelName.split(' ')[0].toLowerCase()} ${this.levelClasses(l.id)}`} onClick={() => this.handleLevelChange(l.id)}>
														<h4 className="signup__level-title">
															<FontAwesomeIcon icon={faCheck} className="signup__level-icon" />
															{l.levelName.split(' ')[0]}
														</h4>

														{false ? (
															<p className="signup__level-price">
																<Localized code="page.signup.text.amountPerMonth" amount={freeUpgrade ? 0 : l.levelAmount} />
															</p>
														) : (
															<p className="signup__level-price">
																<Localized code="page.signup.text.amountPerYear" amount={freeUpgrade ? 0 : (l.levelAmount * 12)} />
															</p>
														)}

														{content['levelFeatures' + l.levelName.split(' ')[0]] ? (
														<div className="signup__level-description cms-generated" dangerouslySetInnerHTML={{__html: content['levelFeatures' + l.levelName.split(' ')[0]].localData[locale].contentValue}} />
														) : null}

														{subscriptionInfo ? (
														<Fragment>
															<p className="signup__level-total">
																<Localized code="page.signup.text.total" />
															</p>
															<p className="signup__level-total-amount">{(freeUpgrade ? 0 : l.levelAmount) * subscriptionInfo.subscriptionMonths} AED</p>
															<p className="signup__repeat">
																<Localized code="page.signup.text.fragment.every" period={locale === 'ar' ? subscriptionInfo.subscriptionNameAr : subscriptionInfo.subscriptionNameEn} />
															</p>
														</Fragment>
														) : null}
													</div>
												))}
											</div>
										</div>

										<h4 className="form__title">
											<Localized code="page.signup.subtitle.subscription" />
										</h4>

										<div className="form__group">
											<label className="form__label">
												<Localized code="page.signup.label.subscriptionPeriod" />
											</label>
											<div className="form__radios form__radios--inline">
												{subscriptions.slice().sort((a, b) => a.subscriptionMonths > b.subscriptionMonths ? -1 : 1).filter(s => 
													selectedLevel && 
													(
														//((selectedLevel.id === 1 || selectedLevel.id === 2) && s.subscriptionMonths === 12) || 
														((selectedLevel.id === 1 || selectedLevel.id === 2) && (s.subscriptionMonths === 3 || s.subscriptionMonths === 6 || s.subscriptionMonths === 12)) || 
														((selectedLevel.id !== 1 && selectedLevel.id !== 2) && (s.subscriptionMonths === 3 || s.subscriptionMonths === 6 || s.subscriptionMonths === 12))
													)).map(s => (
													<FormRadioButton key={s.id} name="subscription" value={s.id} label={locale === 'ar' ? s.subscriptionNameAr : s.subscriptionNameEn} current={subscription} onChange={this.handleSubscriptionChange} />	
												))}
											</div>
										</div>

										{!freeUpgrade ? (
											<Fragment>
												<h4 className="form__title">
													<Localized code="page.signup.subtitle.billingDetails" />
												</h4>

												<div className="form__row">
													<div className="form__column">
														<div className="form__group">
															<label className="form__label" htmlFor="address">
																<Localized code="page.cart.label.address" />
															</label>
															<input className="form__input" type="text" id="address" value={address} onChange={this.handleAddressChange} />
														</div>
													</div>

													{false ? (
														<div className="form__column form__column--quarter">
															<div className="form__group">
																<label className="form__label" htmlFor="postalCode">
																	<Localized code="page.cart.label.postalCode" />
																</label>
																<input className="form__input" type="text" id="postalCode" value={postalCode} onChange={this.handlePostalCodeChange} />
															</div>
														</div>
													) : null}
												</div>

												<div className="form__row">
													<div className="form__column">
														<div className="form__group">
															<label className="form__label" htmlFor="country">
																<Localized code="page.cart.label.country" />
															</label>
															<div className="form__select-wrap">
																<select className="form__select" id="country" value={country} onChange={this.handleCountryChange}>
																	<option value="">{translateMessage('generic.label.pleaseSelect')}</option>
																	{countries.map(country => (
																		<option key={country.countryCode2c} value={country.countryCode2c}>{locale === 'ar' ? country.countryNameAr : country.countryName}</option>
																	))}
																</select>
															</div>
														</div>
													</div>

													<div className="form__column">
														<div className="form__group">
															<label className="form__label" htmlFor="city">
																<Localized code="page.cart.label.city" />
															</label>
															<input className="form__input" type="text" id="city" value={city} onChange={this.handleCityChange} />
														</div>
													</div>
												</div>

												<h4 className="form__title">
													<Localized code="page.signup.subtitle.paymentDetails" />
												</h4>

												<div className="form__row">
													<div className="form__column form__column--quarter">
														<div className="form__group">
															<label className="form__label" htmlFor="firstName">
																<Localized code="page.cart.label.firstName" />
															</label>
															<input className="form__input" type="text" id="firstName" value={firstName} onChange={this.handleFirstNameChange} />
														</div>
													</div>

													<div className="form__column form__column--quarter">
														<div className="form__group">
															<label className="form__label" htmlFor="lastName">
																<Localized code="page.cart.label.lastName" />
															</label>
															<input className="form__input" type="text" id="lastName" value={lastName} onChange={this.handleLastNameChange} />
														</div>
													</div>

													<div className="form__column">
														<div className="form__group">
															<label className="form__label">
																<Localized code="page.signup.label.ccDetails" />
															</label>
															<CreditCardInput
																containerClassName="form__cc-container"
																fieldClassName="form__cc-field"
																inputClassName="form__cc-input"
																dangerTextClassName="form__field-error"
																cardNumberInputProps={{ value: ccNumber, onChange: this.handleCcNumberChange }}
																cardExpiryInputProps={{ value: ccExpiry, onChange: this.handleCcExpiryChange }}
																cardCVCInputProps={{ value: ccCvv, onChange: this.handleCcCvvChange }}
															/>
														</div>
													</div>
												</div>

												<div className="form__group">
													<div className="form__checkboxes">
														<FormCheckboxButton name="termsRefunds" value="1" label={translateMessage('page.signup.label.acceptNoRefunds', {
															__links: {
																link1: <Link className="form__label-link" to="/page/tnc" target="_blank" />
															}
														})} checked={termsRefunds} onChange={this.handleTermsRefundsChange} />
													</div>
												</div>
											</Fragment>
										) : null}

										<div className="form__buttons">
											<button disabled={upgradeDisabled || loading || threeDSSetupInProgress} className="form__button form__button--gold form__button--has-loading" type="submit" onClick={this.handleUpgradeClick}>
												{loading ? (
													<ReactLoading className="form__button-loading" type="spin" color="#FFF" height="20px" width="20px" />
												) : null}
												<Localized code="page.upgrade.button.upgrade" />
											</button>
										</div>
									</div>
									) : null}
								</form>
							</div>
						</div>
					</div>

					<Modal
						isOpen={this.state.challengeOpen}
						overlayClassName="modal"
						className="modal__content modal__content--challenge"
						ariaHideApp={false}
						bodyOpenClassName="locked"
						onAfterOpen={() => {
							// Setup event listeners
							document.getElementById('step-up-iframe').addEventListener('load', this.redirectHandler);

							document.getElementById('step-up-form').submit()
						}}
					>
						<iframe id="step-up-iframe" name="step-up-iframe" height="100%" width="100%"></iframe>
						<form id="step-up-form" target="step-up-iframe" method="post" action={this.state.stepUpUrl}>
							<input type="hidden" name="JWT" value={this.state.accessToken} />
							<input type="hidden" name="MD" value="fazaa" />
						</form>
					</Modal>
				</section>
			);
		}

	}
));

export default Upgrade;
